/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type { FlagPanel, FlagRules } from "~/features/feature-flags/client/model/flagPanel"
import {
  blockedByReason,
  countTargets,
  needsLabel,
  onOffLabel,
} from "~/features/feature-flags/client/model/labels"
import type { FeatureFlagKey } from "~/features/feature-flags/shared/registry"
import type {
  FlagSnapshot,
  FlagValueSource,
  ResolvedFlag,
} from "~/features/feature-flags/shared/snapshot"

export type FlagState = "on" | "off" | "partial"

export const flagState = (value: boolean): FlagState => (value ? "on" : "off")

/** Sort order for a status column: Off, then Partial, then On. */
export const FLAG_STATE_ORDER: Record<FlagState, number> = { off: 0, partial: 1, on: 2 }

/** A flag across companies, for a user or for the companies themselves. */
export type FlagStatus = {
  /** On or off when every company agrees, else partial. Without companies, the value with none. */
  state: FlagState
  companiesOn: number
  companyCount: number
  /** The first prerequisite that is off in one of the companies. */
  blockedBy: string | null
}

/** One company's own setting. Never partial. */
export type CompanyStatus = {
  state: FlagState
  source: FlagValueSource
  blockedBy: string | null
  /** A custom flag on for a company it wasn't built for. */
  offList: boolean
}

function stateOf(flags: readonly ResolvedFlag[]): FlagState {
  if (flags.every((flag) => flag.value)) return "on"
  if (flags.some((flag) => flag.value)) return "partial"
  return "off"
}

/**
 * What a user gets in every company they belong to (`orgIds`), or with no company when they
 * belong to none. `userId` null resolves the companies themselves.
 */
export function userStatus(
  snapshot: FlagSnapshot,
  flagKey: FeatureFlagKey,
  userId: string | null,
  orgIds: readonly string[],
): FlagStatus {
  const flags = orgIds.map((orgId) => snapshot.resolve(orgId, userId)[flagKey])
  const resolved = flags.length > 0 ? flags : [snapshot.resolve(null, userId)[flagKey]]
  return {
    state: stateOf(resolved),
    companiesOn: flags.filter((flag) => flag.value).length,
    companyCount: orgIds.length,
    blockedBy: resolved.find((flag) => flag.blockedBy)?.blockedBy ?? null,
  }
}

/** A flag across every company. With no companies it is the default's status. */
export const flagStatus = (panel: FlagPanel, flagKey: FeatureFlagKey): FlagStatus =>
  userStatus(
    panel.snapshot,
    flagKey,
    null,
    panel.companies.map(({ id }) => id),
  )

export function companyStatus(
  panel: FlagRules,
  flagKey: FeatureFlagKey,
  orgId: string,
): CompanyStatus {
  const flag = panel.snapshot.resolve(orgId)[flagKey]
  return {
    state: flagState(flag.value),
    source: flag.source,
    blockedBy: flag.blockedBy,
    offList: flag.value && panel.isOffList(flagKey, orgId),
  }
}

/** "On for 3 of 39 companies.", "On for every company." or, without companies, "Off by default." */
export function companiesDescription({
  state,
  companiesOn,
  companyCount,
}: Omit<FlagStatus, "blockedBy">): string {
  if (state === "partial") {
    return `On for ${companiesOn} of ${countTargets(companyCount, "company")}.`
  }
  const scope = companyCount > 0 ? "for every company" : "by default"
  return `${onOffLabel(state === "on")} ${scope}.`
}

const SOURCE_LABELS: Record<FlagValueSource, string> = {
  user: "User override",
  org: "Company override",
  default: "Environment default",
  code: "Code default",
}

/** A Source cell: "Needs Demand plan" with the reason when a prerequisite keeps the flag off,
 * else where the value comes from. */
export function flagSourceText({
  source,
  blockedBy,
}: Pick<CompanyStatus, "source" | "blockedBy">): {
  label: string
  reason: string | null
} {
  if (blockedBy) return { label: needsLabel(blockedBy), reason: blockedByReason(blockedBy) }
  return { label: SOURCE_LABELS[source], reason: null }
}
